import React, { useState, useEffect } from 'react';
import { useAutoGoogleAuth } from '../hooks/useAutoGoogleAuth';
import { useAuth } from '../contexts/AuthContext';
import { AlertCircle, X } from "lucide-react";

interface AutoReconnectionNotificationProps {
  className?: string;
}

export const AutoReconnectionNotification: React.FC<AutoReconnectionNotificationProps> = ({ 
  className = "" 
}) => {
  const { licenseData } = useAuth();
  const { isAuthenticated, isLoading, error, autoRefreshEnabled } =
    useAutoGoogleAuth();

  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");

  // Verificar se a empresa tem o módulo de agendamento ativo
  const hasSchedulingModule = licenseData?.mod_agendamento === true;

  useEffect(() => {
    if (!hasSchedulingModule) {
      setShowNotification(false);
      return;
    }

    /**
     * Sem Google Calendar ligado: não mostrar nada (evita toast a cada carga
     * com "Verificando conexão..." enquanto o contexto só confirma desligado).
     */
    if (!isAuthenticated) {
      setShowNotification(false);
      return;
    }

    if (isLoading) {
      setShowNotification(false);
      return;
    }

    if (error) {
      setNotificationMessage(`Erro na conexão com Google Calendar: ${error}`);
      setShowNotification(true);
      return;
    }

    setShowNotification(false);
  }, [isAuthenticated, isLoading, error, hasSchedulingModule]);

  // Auto-hide notification after 5 seconds
  useEffect(() => {
    if (showNotification) {
      const timer = setTimeout(() => {
        setShowNotification(false);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [showNotification]);

  // Se não tem módulo de agendamento ou não deve mostrar notificação, não renderizar
  if (!hasSchedulingModule || !showNotification) {
    return null;
  }

  return (
    <div className={`fixed top-4 right-4 z-50 max-w-sm ${className}`}>
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 shadow-lg">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 shrink-0 text-red-600" />
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900">
              {notificationMessage}
            </p>
            {autoRefreshEnabled && (
              <p className="text-xs text-gray-600 mt-1">
                Verificação automática ativa
              </p>
            )}
          </div>
          <button
            onClick={() => setShowNotification(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
